/** Panel spotlight section: "The Panel" and "The Public Panel" sub-sections. */
import { cn } from '@rocco/utils/cn';
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';
import { HDEPanelSpeakerCard } from './HDEPanelSpeakerCard';

/** Fallback headings used when CMS fields are not yet populated. */
const FALLBACK_PANEL_TITLE = 'THE PANEL';
const FALLBACK_PUBLIC_PANEL_TITLE = '7PM: THE PUBLIC PANEL';

/** Restores spacing and list/link styling stripped by the Tailwind reset for CMS-authored rich text. */
const RICH_TEXT_CLASSES =
    '[&_p+p]:mt-[1em] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-[1.25em] [&_ol]:list-decimal [&_ol]:pl-[1.25em]';

interface PanelSpeaker {
    id: string;
    name: string;
    role: string;
    introduction?: string;
    link?: string;
    imageUrl?: string;
}

interface PanelSpotlightProps {
    panelSpeakers: PanelSpeaker[];
    /** CMS-editable heading for the panel section. Falls back to "THE PANEL" when null. */
    panelDisplayTitle?: string | null;
    /** CMS-editable HTML description for the panel section. */
    panelDescription?: string | null;
    publicPanelSpeakers: PanelSpeaker[];
    /** CMS-editable heading for the public panel section. Falls back to "THE PUBLIC PANEL" when null. */
    publicPanelDisplayTitle?: string | null;
    /** CMS-editable HTML description for the public panel section. Hidden when null. */
    publicPanelDescription?: string | null;
    onRegister?: () => void;
    showRegisterButton?: boolean;
    isMobile?: boolean;
}

export const PanelSpotlight = ({
    panelSpeakers,
    panelDisplayTitle,
    panelDescription,
    publicPanelSpeakers,
    publicPanelDisplayTitle,
    publicPanelDescription,
    onRegister,
    showRegisterButton = true,
    isMobile = false,
}: PanelSpotlightProps) => {
    const panelSpeakersToRender = panelSpeakers;
    const publicPanelSpeakersToRender = publicPanelSpeakers;
    const resolvedPanelTitle =
        panelDisplayTitle?.trim() || FALLBACK_PANEL_TITLE;
    const resolvedPublicPanelTitle =
        publicPanelDisplayTitle?.trim() || FALLBACK_PUBLIC_PANEL_TITLE;
    const hasPublicPanelDescription = Boolean(publicPanelDescription?.trim());

    if (isMobile) {
        return (
            <>
                {panelSpeakersToRender.length > 0 && (
                    <section
                        data-testid="panel-spotlight"
                        className="w-full bg-white px-[1.5rem] pt-[1.25rem] pb-[2rem]"
                    >
                        <h2 className="font-sans text-[1.25rem] leading-[0.9] font-medium text-black uppercase">
                            {resolvedPanelTitle}
                        </h2>
                        {panelDescription?.trim() ? (
                            <div
                                className={cn(
                                    'mt-[0.5rem] text-[0.875rem] leading-[1.4] text-[#141414]',
                                    RICH_TEXT_CLASSES,
                                )}
                                // Description is authored in the CMS by ArchiPro staff.
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: panelDescription,
                                }}
                            />
                        ) : null}
                        <div className="mt-[0.875rem] grid grid-cols-2 gap-x-[0.75rem] gap-y-[1.5rem]">
                            {panelSpeakersToRender.map((speaker) => (
                                <div key={speaker.id}>
                                    {speaker.imageUrl ? (
                                        <img
                                            src={speaker.imageUrl}
                                            alt={speaker.name}
                                            className="h-[14.5rem] w-full object-cover"
                                        />
                                    ) : null}
                                    <p className="mt-[0.5rem] text-[0.6875rem] font-medium tracking-[0.04em] text-[#141414] uppercase">
                                        {speaker.name}
                                    </p>
                                    <p className="text-[0.6875rem] text-[#141414] uppercase">
                                        {speaker.role}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
                {publicPanelSpeakersToRender.length > 0 && (
                    <section
                        data-testid="public-panel"
                        className="w-full bg-white px-[1.5rem] pt-[1.5rem]"
                    >
                        <h2 className="mb-[1rem] font-sans text-[1.25rem] leading-[0.9] font-medium text-[#141414] uppercase">
                            {resolvedPublicPanelTitle}
                        </h2>
                        <div className="relative mx-[-1.5rem] mt-[6rem] bg-[#141414] px-[1.5rem] pt-[6rem] pb-16">
                            <div className="z-10 mt-[-11rem] grid grid-cols-2 gap-x-[0.75rem] gap-y-[0.75rem]">
                                {publicPanelSpeakersToRender.map((speaker) => (
                                    <div key={speaker.id} className="relative">
                                        {speaker.imageUrl ? (
                                            <img
                                                src={speaker.imageUrl}
                                                alt={speaker.name}
                                                className="h-[14.5rem] w-full object-cover"
                                            />
                                        ) : null}
                                        <p className="mt-[0.375rem] text-[0.625rem] font-medium tracking-[0.04em] text-white uppercase">
                                            {speaker.name}
                                        </p>
                                        <p className="text-[0.6875rem] text-[#9a9a9a] uppercase">
                                            {speaker.role}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            {hasPublicPanelDescription ? (
                                <div
                                    className={cn(
                                        'mt-10 text-[0.9375rem] leading-[1.33] text-[#d4d4d4]',
                                        RICH_TEXT_CLASSES,
                                    )}
                                    // Description is authored in the CMS by ArchiPro staff.
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: publicPanelDescription!,
                                    }}
                                />
                            ) : null}
                            <button
                                type="button"
                                onClick={onRegister}
                                className={`mt-[1.5rem] flex h-[3.25rem] w-full items-center justify-center rounded-full bg-white text-[0.9375rem] font-medium text-[#141414] ${showRegisterButton ? '' : 'hidden'}`}
                            >
                                Register Now — Free
                            </button>
                        </div>
                    </section>
                )}
            </>
        );
    }

    const hasPublicPanel = publicPanelSpeakersToRender.length > 0;

    return (
        <div className={cn('empty:h-24', !hasPublicPanel && 'md:pb-[0.5rem]')}>
            {/* Sub-section 1: THE PANEL (white background) */}
            {panelSpeakersToRender.length > 0 && (
                <section
                    data-testid="panel-spotlight"
                    className="relative w-full bg-white pt-[7rem]"
                >
                    <ScrollReveal animation="fade-left" duration={400}>
                        <h2 className="absolute px-12 font-sans text-[2.854rem] leading-[0.9] font-medium text-black uppercase">
                            {resolvedPanelTitle}
                        </h2>
                    </ScrollReveal>
                    <HDEDesktopFrame className="pl-12">
                        <div className="relative flex min-h-[38rem] justify-between pt-24">
                            {/* Left column: description + per-speaker bio list */}
                            <div className="flex w-3xl flex-col pt-12">
                                {panelDescription?.trim() ? (
                                    <ScrollReveal
                                        animation="fade-up"
                                        duration={500}
                                    >
                                        <div
                                            className={cn(
                                                'max-w-188.75 font-sans text-[1.75rem] leading-tight font-medium tracking-[-0.035rem] text-black',
                                                RICH_TEXT_CLASSES,
                                            )}
                                            // Description is authored in the CMS by ArchiPro staff.
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: panelDescription,
                                            }}
                                        />
                                    </ScrollReveal>
                                ) : null}

                                <div className="mt-[3.75rem] flex w-2xl flex-col gap-[3.5rem]">
                                    {panelSpeakersToRender.map((speaker) => (
                                        <div
                                            key={speaker.id}
                                            className="flex flex-col gap-[1rem]"
                                        >
                                            <div className="flex flex-col gap-[0.25rem] tracking-[-0.057875rem] uppercase">
                                                <p className="font-display text-[1.929rem] leading-[0.9] font-bold text-black">
                                                    {speaker.name}
                                                </p>
                                                <p className="font-display text-[1.929rem] leading-[0.9] font-[450] text-[#8f8f8f]">
                                                    {speaker.role}
                                                </p>
                                            </div>
                                            {speaker.introduction ? (
                                                <div
                                                    className={cn(
                                                        'font-sans text-[1.3125rem] leading-tight font-medium tracking-[-0.02625rem] text-black',
                                                        RICH_TEXT_CLASSES,
                                                    )}
                                                    // Introduction is authored in the CMS by ArchiPro staff.
                                                    // eslint-disable-next-line react/no-danger
                                                    dangerouslySetInnerHTML={{
                                                        __html: speaker.introduction,
                                                    }}
                                                />
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right column: portrait photo cards, absolutely positioned */}
                            <div className="grid shrink-0 grid-cols-2 gap-[1.125rem] pr-31">
                                {panelSpeakersToRender.map((speaker, index) => {
                                    return (
                                        <ScrollReveal
                                            key={speaker.id}
                                            animation="fade-up"
                                            duration={600}
                                            delay={index * 120}
                                        >
                                            <HDEPanelSpeakerCard
                                                speaker={speaker}
                                            />
                                        </ScrollReveal>
                                    );
                                })}
                            </div>
                        </div>
                    </HDEDesktopFrame>
                </section>
            )}

            {/* Sub-section 2: THE PUBLIC PANEL (dark background) */}
            {publicPanelSpeakersToRender.length > 0 && (
                <section
                    data-testid="public-panel"
                    className="relative w-full md:pt-[10rem]"
                >
                    <div className="relative bg-[#0e0e0e] md:pb-20">
                        <HDEDesktopFrame className="relative flex justify-between pl-12">
                            {/* Title */}
                            <ScrollReveal
                                animation="fade-in"
                                duration={500}
                                className="absolute top-[6.5625rem] left-[3.125rem]"
                            >
                                <h2 className="font-sans text-[2.854625rem] leading-[0.9] font-medium whitespace-nowrap text-white uppercase">
                                    {resolvedPublicPanelTitle}
                                </h2>
                            </ScrollReveal>

                            <div className="pt-44">
                                {hasPublicPanelDescription ? (
                                    <ScrollReveal
                                        animation="fade-up"
                                        duration={600}
                                        delay={300}
                                        className="w-192.5 pt-15"
                                    >
                                        <div
                                            className={cn(
                                                'font-sans text-[1.75rem] leading-tight font-medium tracking-[-0.035rem] text-white',
                                                RICH_TEXT_CLASSES,
                                            )}
                                            // Description is authored in the CMS by ArchiPro staff.
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: publicPanelDescription!,
                                            }}
                                        />
                                    </ScrollReveal>
                                ) : null}

                                <div className="flex w-2xl flex-col gap-[4.6875rem] pt-28">
                                    {publicPanelSpeakersToRender.map(
                                        (speaker) => (
                                            <div
                                                key={speaker.id}
                                                className="flex flex-col gap-[1rem]"
                                            >
                                                <div className="flex w-[28.75rem] flex-col gap-[0.25rem] tracking-[-0.057875rem] uppercase">
                                                    <p className="font-display text-[1.929625rem] leading-[0.9] font-medium text-white">
                                                        {speaker.name}
                                                    </p>
                                                    <p className="font-display text-[1.929625rem] leading-[0.9] font-[450] text-[#8f8f8f]">
                                                        {speaker.role}
                                                    </p>
                                                </div>
                                                {speaker.introduction ? (
                                                    <div
                                                        className={cn(
                                                            'font-sans text-[1.3125rem] leading-tight font-medium tracking-[-0.02625rem] text-white',
                                                            RICH_TEXT_CLASSES,
                                                        )}
                                                        // Introduction is authored in the CMS by ArchiPro staff.
                                                        // eslint-disable-next-line react/no-danger
                                                        dangerouslySetInnerHTML={{
                                                            __html: speaker.introduction,
                                                        }}
                                                    />
                                                ) : null}
                                            </div>
                                        ),
                                    )}
                                    <button
                                        type="button"
                                        onClick={onRegister}
                                        className={cn(
                                            'flex h-[3.1273125rem] w-fit items-center justify-center rounded-[73.1328125rem] border border-black bg-white px-[2.2338125rem] font-sans text-[1.0580625rem] leading-[1.7125625rem] font-medium text-black',
                                            !showRegisterButton && 'hidden',
                                        )}
                                    >
                                        Register Now – Free
                                    </button>
                                </div>
                            </div>

                            {/* Photo grid sticks up above the dark section. */}
                            <div className="relative top-[-4.75rem] grid grid-cols-2 content-start gap-[1.125rem] pr-31">
                                {publicPanelSpeakersToRender.map(
                                    (speaker, index) => {
                                        return (
                                            <ScrollReveal
                                                key={speaker.id}
                                                animation="scale-settle"
                                                duration={500}
                                                delay={index * 100}
                                            >
                                                <HDEPanelSpeakerCard
                                                    speaker={speaker}
                                                />
                                            </ScrollReveal>
                                        );
                                    },
                                )}
                            </div>
                        </HDEDesktopFrame>
                    </div>
                </section>
            )}
        </div>
    );
};
